import { Box, CircularProgress } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import {
  approveJob,
  calculateServicesForJob,
  createJob,
  deleteJob,
  fetchJob,
  patchJob,
  revertJob,
} from '@sophocles/entities/Job';
import { fetchJobConfigurationForCustomer } from '@sophocles/entities/JobConfiguration/actions';
import { fetchServicesPerformedForJob } from '@sophocles/entities/ServicePerformed';
import { fetchRawSite } from '@sophocles/entities/Site';
import { fetchSiteGroup } from '@sophocles/entities/SiteGroup';
import { loadSitePhotos } from '@sophocles/features/media/api';
import { normalizeBinaryFileRow } from '@sophocles/features/media/normalize';
import { fetchActiveCrews, fetchJobServiceOptions } from '@sophocles/features/projectServices/api';
import { loadReference, selectLightFixtures } from '@sophocles/features/reference';
import { addToast } from '@sophocles/features/toast';
import { determineIsCrewManager, userPermCheck } from '@sophocles/utils';
import WorkForm from '@sophocles/views/services/forms/WorkForm/WorkForm';
import { createJobErrorMessage, updateJobErrorMessage } from '@sophocles/views/services/utils/workFormErrors';

// Fixture ids relevant to project-services work forms (LED and metal halide types).
const WORK_FORM_FIXTURE_IDS = [17, 18, 19, 28, 29];

// Memoized so the filtered array stays referentially stable across renders.
const selectWorkFormLightFixtures = createSelector(selectLightFixtures, fixtures =>
  fixtures.filter(f => WORK_FORM_FIXTURE_IDS.includes(f.id)),
);

function WorkFormView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId, siteId, siteGroupId, projectId, jobId } = useParams();

  const dispatch = useDispatch();

  const { user } = useUser();
  const lightFixtures = useSelector(selectWorkFormLightFixtures);

  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [site, setSite] = useState(null);
  const [siteGroup, setSiteGroup] = useState(null);
  const [job, setJob] = useState(null);
  const [jobConfiguration, setJobConfiguration] = useState(null);
  const [jobInfo, setJobInfo] = useState(null);
  const [images, setImages] = useState(null);
  const [services, setServices] = useState(null);
  const [serviceOptions, setServiceOptions] = useState(null);
  const [crews, setCrews] = useState(null);

  useEffect(() => {
    dispatch(fetchJobConfigurationForCustomer(customerId)).then(res => {
      if (res && res.configuration) {
        setJobConfiguration(res.configuration);
      }
    });

    dispatch(fetchRawSite(siteId)).then(site => {
      setSite(site.toJS());
    });

    dispatch(loadReference());

    dispatch(fetchSiteGroup(siteGroupId))
      .then(res => res.siteGroups.first())
      .then(siteGroup => setSiteGroup(siteGroup.toJS()));

    fetchActiveCrews()
      .then(data => {
        const results = data?.data || data?.results || [];
        setCrews(results);
      })
      .catch(error => {
        console.error('Error fetching crews:', error);
        setCrews([]);
      });

    fetchJobServiceOptions()
      .then(data => {
        const results = data?.data ?? [];
        setServiceOptions(
          results
            .filter(option => option.service_type === 'panel')
            .map(option => ({
              id: option.id,
              label: option.name,
            })),
        );
      })
      .catch(error => {
        console.error('Error fetching job services:', error);
        setServiceOptions([]);
      });

    dispatch(loadSitePhotos({ siteId, queryParams: {} })).then(action => {
      const results = action.payload?.data ?? [];
      // Reconcile binary-files shape to the shape ImageTable expects:
      //   upload_time  ← timestamp  (column sort key)
      //   upload_user  ← uploaded_by  (column displays first_name/last_name)
      setImages(
        results.map(row => ({
          ...normalizeBinaryFileRow(row),
          upload_time: row.timestamp,
          upload_user: row.uploaded_by,
        })),
      );
    });

    if (jobId) {
      dispatch(fetchJob(jobId))
        .then(res => {
          if (_.has(res, 'error')) {
            navigate(`/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/`);
          }
          setJob(res);
          if (!res.pending) {
            dispatch(fetchServicesPerformedForJob(res.id))
              .then(servicesRes => {
                setServices(servicesRes.results);
              })
              .catch(error => {
                console.error('Error fetching services performed on initial load:', error);
                setServices([]);
              });
          } else {
            dispatch(calculateServicesForJob(jobId, res))
              .then(servicesRes => {
                setServices(servicesRes);
              })
              .catch(error => {
                console.error('Error calculating services on initial load:', error);
                setServices([]);
              });
          }
        })
        .catch(error => {
          console.error('Error fetching job on initial load:', error);
          navigate(`/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/`);
        });
    } else {
      setServices([]);
    }
  }, []);

  useEffect(() => {
    setJobInfo(
      job || {
        latitude_entered: _.get(site, 'latitude', 0),
        longitude_entered: _.get(site, 'longitude', 0),
        utility_company: _.get(site, ['configuration', 'utility_company'], ''),
        utility_meter_number: _.get(site, ['configuration', 'utility_meter_number'], ''),
      },
    );
  }, [job, site]);

  useEffect(() => {
    if (jobConfiguration && site && siteGroup && images && crews && serviceOptions && jobInfo && services) {
      console.log('All data loaded, setting loading to false:', {
        jobConfiguration: !!jobConfiguration,
        site: !!site,
        siteGroup: !!siteGroup,
        images: !!images,
        crews: !!crews,
        serviceOptions: !!serviceOptions,
        jobInfo: !!jobInfo,
        services: !!services,
      });
      setLoading(false);
    } else {
      console.log('Still loading, missing data:', {
        jobConfiguration: !!jobConfiguration,
        site: !!site,
        siteGroup: !!siteGroup,
        images: !!images,
        crews: !!crews,
        serviceOptions: !!serviceOptions,
        jobInfo: !!jobInfo,
        services: !!services,
      });
    }
  }, [jobConfiguration, site, siteGroup, images, crews, serviceOptions, jobInfo, services]);

  let pageTitle = t('projectServices.workForm.pageTitle', 'Work Form');

  if (!loading) {
    const siteGroupName = _.get(siteGroup, 'name', '');
    const siteGroupDescription = _.get(siteGroup, 'description', '');
    const siteName = _.get(site, 'name', '');
    pageTitle =
      (siteGroupName !== '' ? siteGroupName + '. ' + siteGroupDescription + ' - ' : '') +
      (siteName ? ': ' + siteName : '');
  }

  return (
    <AppNav notFullHeight loading={loading} page="services_projects_work_form">
      <Box sx={{ top: '64px', mx: 2, pb: '24px' }}>
        <TitleBar showBackButton title={loading ? <CircularProgress /> : pageTitle} />
        {loading || services == null || jobConfiguration == null || crews == null ? (
          <LoadingScreen />
        ) : (
          <WorkForm
            site={site}
            siteGroup={siteGroup}
            jobInfo={jobInfo}
            jobConfiguration={jobConfiguration}
            services={services}
            servicesLoading={servicesLoading}
            serviceOptions={serviceOptions}
            crews={crews}
            images={images}
            lightFixtures={lightFixtures}
            canConfirm={user.get('is_superuser', false) || determineIsCrewManager(user.get('groups', []))}
            canDelete={userPermCheck(user, 'odl_project.delete_projectjob')}
            onDelete={() => {
              return dispatch(deleteJob(jobId)).then(result => {
                if (result) {
                  navigate(`/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/`);
                }
              });
            }}
            onCreate={payload => {
              return dispatch(createJob(payload)).then(res => {
                if (res.error) {
                  dispatch(
                    addToast({
                      message: createJobErrorMessage(res.error, t),
                      level: 'error',
                    }),
                  );
                } else {
                  setJob(res);
                  navigate(
                    `/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/site/${siteId}/job/${res.id}/`,
                  );
                }
              });
            }}
            onApprove={payload => {
              setServicesLoading(true);
              return dispatch(approveJob(jobId, payload))
                .then(res => {
                  setJob(res);
                  // After approval, fetch the services performed for the job
                  if (!res.pending) {
                    dispatch(fetchServicesPerformedForJob(res.id))
                      .then(servicesRes => {
                        setServices(servicesRes.results);
                        setServicesLoading(false);
                      })
                      .catch(error => {
                        console.error('Error fetching services after approval:', error);
                        setServicesLoading(false);
                      });
                  } else {
                    setServicesLoading(false);
                  }
                })
                .catch(error => {
                  console.error('Error approving job:', error);
                  setServicesLoading(false);
                });
            }}
            onRevert={payload => {
              setServicesLoading(true);
              return dispatch(revertJob(jobId, payload))
                .then(res => {
                  setJob(res);
                  // After revert, job becomes pending again, so calculate services
                  if (res.pending) {
                    dispatch(calculateServicesForJob(jobId, res))
                      .then(servicesRes => {
                        setServices(servicesRes);
                        setServicesLoading(false);
                      })
                      .catch(error => {
                        console.error('Error calculating services after revert:', error);
                        setServicesLoading(false);
                      });
                  } else {
                    setServicesLoading(false);
                  }
                })
                .catch(error => {
                  console.error('Error reverting job:', error);
                  setServicesLoading(false);
                });
            }}
            onUpdate={payload => {
              return dispatch(patchJob(jobId, payload)).then(res => {
                if (res.error) {
                  dispatch(
                    addToast({
                      message: updateJobErrorMessage(res.error, t),
                      level: 'error',
                    }),
                  );
                  return;
                }
                setJob(res);
              });
            }}
            onCalculate={payload => {
              return dispatch(calculateServicesForJob(jobId, payload)).then(res => {
                setServices(res);
              });
            }}
          />
        )}
      </Box>
    </AppNav>
  );
}

export default WorkFormView;
